import { webApiEndpoints } from "../config"
import { mapper } from "../helpers/mapper.helper"
import secureHelper from "../helpers/secure.helper"
import {
    RepositoryApiSubTypes,
    ApiResource,
    ResourceType,
    ApiResourceVersion,
    ResourceVersionArtifactType,
    ResourceVersionType,
    RepositoryGroup,
    AppRequestType,
    AppCategories,
} from "../types"

const category: AppCategories = 'resources'

export const resourcesAdapter = {
    getResourcesBySubtype: (subtipo: RepositoryApiSubTypes, afore: string): AppRequestType<RepositoryGroup<ResourceType>[]>[typeof category]['getResourcesBySubtype'] => ({
        endpoint: webApiEndpoints[category].getResourcesBySubtype,
        input: ({ id: subtipo }),
        parameters: { headers: { afore } },
        keepCacheBeforeNewRequest: true,
        output: (datos) => datos.map(mapper.apiRepository)
    }),
    getResourceListBySubtype: (subtipo: RepositoryApiSubTypes, afore: string): AppRequestType<ResourceType[]>[typeof category]['getResourcesBySubtype'] => ({
        endpoint: webApiEndpoints[category].getResourcesBySubtype,
        input: ({ id: subtipo }),
        parameters: { headers: { afore } },
        keepCacheBeforeNewRequest: true,
        output: (datos) => datos.reduce((prev, currentVal) => [...prev, ...mapper.apiResourceList(currentVal)], [] as ResourceType[])
    }),
    getResourceListByRol: (idRol: number, afore: string): AppRequestType<ResourceType[]>[typeof category]['getResourcesByRol'] => ({
        endpoint: webApiEndpoints[category].getResourcesByRol,
        input: ({ id: String(idRol) }),
        parameters: { headers: { afore } },
        keepCacheBeforeNewRequest: true,
        output: (datos) => datos.map(mapper.apiResource)
    }),
    getResourcesByName: (name: string): AppRequestType<ResourceType>[typeof category]['getResourcesByName'] => ({
        endpoint: webApiEndpoints[category].getResourcesByName,
        input: ({ nombre: name }),
        keepCacheBeforeNewRequest: true,
        hideError: true,
        output: (datos) => mapper.apiResource(datos)
    }),
    addResource: (data: ResourceType, files: File[]): AppRequestType<number>[typeof category]['addResource'] => {
        const formData = new FormData()
        const input: ApiResource = {
            descripcion: data.description,
            etiqueta: data.label,
            id: data.id,
            idRepositorio: data.idRepository,
            nombre: data.name || '',
            versiones: mapVersion(data),
            menuAsociado: data.linkedMenus || []
        }
        formData.append("input", JSON.stringify(input))
        files?.forEach(f => formData.append("artefactos", f))
        return ({
            endpoint: webApiEndpoints[category].addResource,
            input: (formData),
            output: (datos) => datos.id
        })
    },
    linkResources: (data: ResourceType[]): AppRequestType<string>[typeof category]['linkResources'] => {
        const input: ApiResource[] = data.map(r => ({
            descripcion: r.description,
            etiqueta: r.label,
            id: r.id,
            idRepositorio: r.idRepository,
            nombre: r.name || '',
            versiones: mapVersion(r),
            menuAsociado: r.linkedMenus || [],
            aforeGeneral: r.generalAfore?.join(',')
        }))
        return ({
            endpoint: webApiEndpoints[category].linkResources,
            input: (input),
            output: (datos) => datos
        })
    },
    editResource: (data: ResourceType): AppRequestType<number>[typeof category]['editResource'] => ({
        endpoint: webApiEndpoints[category].editResource,
        input: ({
            descripcion: data.description,
            etiqueta: data.label,
            id: data.id,
            idRepositorio: data.idRepository,
            nombre: data.name || '',
            versiones: [],
            menuAsociado: data.linkedMenus || [],
            aforeGeneral: data.generalAfore?.join()
        }),
        output: (datos) => datos.id
    }),
    deleteResource: (data: ResourceType): AppRequestType<number>[typeof category]['deleteResource'] => ({
        endpoint: webApiEndpoints[category].deleteResource,
        input: ({
            id: data.id,
            versiones: [],
            descripcion: '',
            etiqueta: '',
            idRepositorio: data.idRepository,
            nombre: data.name
        }),
        output: (datos) => datos
    }),
    updateVersion: (data: ResourceVersionType, files: File[], idResource?: number): AppRequestType<number>[typeof category]['addVersion'] => {
        const formData = new FormData()
        const input: ApiResourceVersion = {
            id: data.id,
            nota: data.note,
            version: data.version,
            normativo: data.normative,
            afores: data.afores.join(),
            artefactos: data.artifacts.map(a => ({
                id: a.id,
                nombre: a.name,
                tipoArtefacto: a.type,
                rutaArtefacto: a.path
            }))
        }
        formData.append("input", JSON.stringify(input))
        files?.forEach(f => formData.append("artefactos", f))
        return ({
            endpoint: webApiEndpoints[category][idResource ? 'addVersion' : 'editVersion'],
            input: formData,
            parameters: idResource ? { headers: { idResource: idResource.toString() } } : undefined,
            output: (datos) => datos.id
        })
    },
    deleteVersion: (data: ResourceVersionType): AppRequestType<number>[typeof category]['deleteVersion'] => ({
        endpoint: webApiEndpoints[category].deleteVersion,
        input: ({
            id: data.id,
            artefactos: [],
            afores: '',
            nota: data.note,
            version: data.version,
            normativo: data.normative
        }),
        output: (datos) => datos
    }),
    deleteArtifact: (artifact: ResourceVersionArtifactType): AppRequestType<number>[typeof category]['deleteArtifact'] => ({
        endpoint: webApiEndpoints[category].deleteArtifact,
        input: ({
            id: artifact.id,
            nombre: artifact.name,
            tipoArtefacto: artifact.type
        }),
        output: (datos) => datos
    }),
    downloadResource: (idRepository: number, idArtifact: number, callBack: (data: Blob) => void): AppRequestType<void>[typeof category]['downloadArtifact'] => ({
        endpoint: webApiEndpoints[category].downloadArtifact,
        input: ({ idRepositorio: idRepository, idArtefacto: idArtifact }),
        isSuccesful: (resp) => resp.status === 200,
        output: (data) => {
            data.arrayBuffer().then(rawData => callBack(secureHelper.decriptFile(rawData)))
        }
    })
}


function mapVersion(resource: ResourceType): ApiResourceVersion[] {
    return resource.versions.map(v => ({
        id: v.id,
        nota: v.note,
        version: v.version,
        afores: v.afores.join(),
        normativo: v.normative,
        artefactos: v.artifacts.map(a => ({
            id: a.id,
            nombre: a.name,
            tipoArtefacto: a.type,
            rutaArtefacto: a.path
        }))
    }))
}